import React from 'react';
import { Text, Flex, FlexProps } from 'pounceV2';
import { useTheme } from '@mui/material';
import CompoundInputV3 from './CompoundInputV3';

export type CompoundNumberRangeInputProps = FlexProps & {
  /**
   * The value of the input number.
   * */
  values: number[] | null;

  /** The max number that the input can accept */
  max?: number;

  /** The min number that the input can accept */
  min?: number;

  /** The callback function that is triggered when the value of the input changes. */
  onChange: (value: number[]) => void;

  /** Refs for inputs */
  fromInputRef?: React.Ref<HTMLInputElement>;
  toInputRef?: React.Ref<HTMLInputElement>;
};

function sanitizedValue(v: number, min: number, max: number): number {
  if (Number.isNaN(v)) {
    return undefined;
  }
  if (v === undefined) {
    return undefined;
  }
  if (v > max) {
    return max;
  }
  if (v < min) {
    return min;
  }
  return v;
}

const CompoundNumberRangeInputV3 = React.forwardRef<HTMLDivElement, CompoundNumberRangeInputProps>(
  function CompoundNumberRangeInput(
    props: CompoundNumberRangeInputProps,
    ref,
  ): React.ReactElement<CompoundNumberRangeInputProps> {
    const {
      palette: { tokens },
    } = useTheme();
    const {
      values,
      max = Number.MAX_SAFE_INTEGER,
      min = Number.MIN_SAFE_INTEGER,
      onChange,
      fromInputRef,
      toInputRef,
      ...rest
    } = props;

    const [v1, v2] = React.useMemo(() => {
      return values || [undefined, undefined];
    }, [values]);

    const handleChange = React.useCallback(
      (v: number, i: number) => {
        const nv = sanitizedValue(v, min, max);
        const newValues = i === 0 ? [nv, v2] : [v1, nv];
        onChange(newValues);
      },
      [onChange, v1, v2, min, max],
    );

    return (
      <Flex ref={ref} spacing={1}>
        <CompoundInputV3.Input
          {...rest}
          ref={fromInputRef}
          type="number"
          placeholder="From"
          value={v1}
          aria-valuenow={v1}
          max={max}
          aria-valuemax={max}
          min={min}
          aria-valuemin={min}
          onChange={e => handleChange(e.target.valueAsNumber, 0)}
        />
        <Text color={tokens.content.text.neutral[3]} fontSize="medium">
          and
        </Text>
        <CompoundInputV3.Input
          {...rest}
          ref={toInputRef}
          type="number"
          placeholder="To"
          value={v2}
          aria-valuenow={v2}
          max={max}
          aria-valuemax={max}
          min={min}
          aria-valuemin={min}
          onChange={e => handleChange(e.target.valueAsNumber, 1)}
        />
      </Flex>
    );
  },
);

export default CompoundNumberRangeInputV3;
